import { isToday } from 'date-fns';
import React, { useState } from 'react';

// Define an interface for the expected payload structure (remains the same)
interface CallPayload {
  name: string;
  phone_number: string;
  vehicle_name: string;
  language: string;
  license_plate: string;
  collection_name: string;
  calling_number: string;
  bot_name: string;
  bot_intro?: string; // Optional field for bot introduction
  notification: { // New notification object
    sms: boolean;
    whatsapp: boolean;
  };
  custom_fields?: { [key: string]: string }; // New field for dynamic labels
}

interface CallFormProps {
  onSubmit: (payload: CallPayload) => void;
  isLoading: boolean;
}

// --- Data for Dropdowns (only for Vehicle Name, Language, Collection Name) ---
const vehicleNamesList: string[] = ["Changan", "Hyundai", "Peugeot"];
const languages: string[] = ["English", "Arabic"]; // Example languages
const collectionNames: string[] = ["AlMajdouie"];

// --- InboundInfoCard Component (NEWLY CREATED) ---
const InboundInfoCard: React.FC = () => {
  return (
    // The h-full class makes sure this div takes up the full height available in its grid cell
    <div className="p-6 bg-blue-50 border border-blue-200 rounded-lg shadow-md flex flex-col justify-start h-full">
      <h3 className="text-xl font-semibold text-blue-800 mb-3 text-center">For Inbound Service Booking</h3>
      <p className="text-gray-700 mb-2 text-center">To test inbound calls or book a service, please call our dedicated number:</p>
      <div className="flex flex-col items-center justify-center space-y-2">
        <div className="flex items-center text-lg font-bold text-blue-700">
          <svg xmlns="http://www.w3.org/2000/svg" className="h-6 w-6 mr-2 text-blue-600" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}>
            <path strokeLinecap="round" strokeLinejoin="round" d="M3 5a2 2 0 012-2h3.28a1 1 0 01.948.684l1.498 4.493a1 1 0 01-.502 1.21l-2.257 1.13a11.042 11.042 0 005.516 5.516l1.13-2.257a1 1 0 011.21-.502l4.493 1.498a1 1 0 01.684.949V19a2 2 0 01-2 2h-1C9.716 21 3 14.284 3 6V5z" />
          </svg>
          <span className="text-gray-900">Call:</span> <a href="tel:+12345952228" className="ml-2 text-blue-700 hover:underline">+12345952228</a>
        </div>
        <p className="text-sm text-white-600 text-center">This number is specifically for service booking demonstrations and testing purposes.</p>
      </div>
    </div>
  );
};

// --- CallForm Component (MODIFIED to only contain outbound form logic and UI) ---
const CallForm: React.FC<CallFormProps> = ({ onSubmit, isLoading }) => {
  // --- State for all form fields ---
  const [name, setName] = useState('');
  // Set default values for Phone Number and License Plate
  const [phoneNumber, setPhoneNumber] = useState('+966501234567'); // Default phone number
  const [vehicleName, setVehicleName] = useState(vehicleNamesList[0]); // Now a standalone dropdown
  const [language, setLanguage] = useState(languages[0]); // Selected Language
  // Set default value for License Plate
  const [licensePlate, setLicensePlate] = useState('7184LBB');
  const [collectionName, setCollectionName] = useState(collectionNames[0]);
  const [callingNumber, setCallingNumber] = useState('');
  const [botName, setBotName] = useState('Salma');
  const [botIntro, setBotIntro] = useState('We are glad to call you...');

  // State for notifications
  const [sendSms, setSendSms] = useState(false);
  const [sendWhatsapp, setSendWhatsapp] = useState(false);

  // State to control visibility of custom fields
  const [showCustomFields, setShowCustomFields] = useState(false);
  const [customLabels, setCustomLabels] = useState<{ label: string; value: string }[]>([]);

  const [error, setError] = useState('');

  // --- Event Handlers ---
  const handlePhoneNumberChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    setPhoneNumber(event.target.value);
    setError('');
  };

  const handleLicensePlateChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    setLicensePlate(event.target.value);
    setError('');
  };

  const handleVehicleNameChange = (event: React.ChangeEvent<HTMLSelectElement>) => {
    setVehicleName(event.target.value);
    setError('');
  };

  const handleLanguageChange = (e: React.ChangeEvent<HTMLSelectElement>) => {
    const selectedLanguage = e.target.value;
    setLanguage(selectedLanguage);
    setError('');
  };

  const handleCollectionChange = (e: React.ChangeEvent<HTMLSelectElement>) => {
    setCollectionName(e.target.value);
    setError('');
  }

  // Handler for toggling custom fields section
  const handleToggleCustomFields = () => {
    setShowCustomFields(!showCustomFields);
    if (showCustomFields) {
      setCustomLabels([]); // Clear custom fields when hiding
    }
  };

  // Handlers for dynamic labels
  const handleAddLabel = () => {
    setCustomLabels([...customLabels, { label: '', value: '' }]);
  };

  const handleCustomLabelChange = (index: number, type: 'label' | 'value', e: React.ChangeEvent<HTMLInputElement | HTMLSelectElement>) => {
    const newCustomLabels = [...customLabels];
    newCustomLabels[index][type] = e.target.value;
    setCustomLabels(newCustomLabels);
    setError('');
  };

  const handleRemoveLabel = (index: number) => {
    const newCustomLabels = customLabels.filter((_, i) => i !== index);
    setCustomLabels(newCustomLabels);
  };

  // Basic phone number validation
  const validatePhoneNumber = (number: string) => {
    const cleaned = number.replace(/\D/g, '');
    return cleaned.length >= 10;
  };

  // Basic license plate validation
  const validateLicensePlate = (plate: string) => {
    return plate.trim().length > 0;
  };

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    setError('');

    // --- Validation ---
    if (!name.trim()) return setError('Customer Name is required.');
    if (!callingNumber.trim()) return setError('Your Calling Number is required.');
    if (!validatePhoneNumber(callingNumber)) return setError('Invalid Your Calling Number format.');
    if (!phoneNumber.trim()) return setError('Customer Phone Number is required.');
    if (!validatePhoneNumber(phoneNumber)) return setError('Invalid Customer Phone Number format.');
    if (!licensePlate.trim()) return setError('License Plate is required.');
    if (!validateLicensePlate(licensePlate)) return setError('Invalid License Plate format.');
    if (!vehicleName) return setError('Please select a Vehicle Name.');
    if (!botName.trim()) return setError('Bot Name is required.');
    if (!botIntro.trim()) return setError('Bot intro is required.');
    if (!language) return setError('Please select a Language.');
    if (!collectionName) return setError('Please select a Collection Name.');

    // Validate custom labels ONLY if they are enabled/visible
    const customFieldsPayload: { [key: string]: string } = {};
    if (showCustomFields) {
      for (const item of customLabels) {
        if (item.label.trim() && item.value.trim()) {
          customFieldsPayload[item.label.trim()] = item.value.trim();
        } else if (item.label.trim() || item.value.trim()) {
          // Error if only one part of a custom field is filled
          return setError('All custom labels and values must be filled, or both left empty, in the custom fields section.');
        }
      }
    }

    // --- Construct Payload ---
    const payload: CallPayload = {
      name: name.trim(),
      phone_number: phoneNumber.trim(),
      vehicle_name: vehicleName,
      language: language,
      license_plate: licensePlate.trim(),
      collection_name: collectionName,
      calling_number: callingNumber.trim(),
      bot_name: botName.trim(),
      bot_intro: botIntro.trim(),
      notification: { // Include notification preferences
        sms: sendSms,
        whatsapp: sendWhatsapp,
      },
      // Only include custom_fields if they are enabled and there's data
      ...(showCustomFields && Object.keys(customFieldsPayload).length > 0 && { custom_fields: customFieldsPayload }),
    };

    // --- Submit ---
    onSubmit(payload);
  };

  // Helper for standard input props
  const getInputProps = (
    value: string,
    onChange: (e: React.ChangeEvent<HTMLInputElement>) => void,
    placeholder: string,
    type: string = 'text',
    required: boolean = false,
    readOnly: boolean = false
  ) => ({
    type: type,
    placeholder: placeholder,
    className: `w-full px-4 py-2 border border-gray-300 rounded-lg shadow-sm focus:outline-none focus:ring-2 focus:ring-blue-500 ${readOnly ? 'bg-gray-100 cursor-not-allowed' : ''}`,
    value: value,
    onChange: onChange,
    disabled: isLoading,
    required: required,
    readOnly: readOnly
  });

  // Helper for select props
  const getSelectProps = (
    value: string,
    onChange: (e: React.ChangeEvent<HTMLSelectElement>) => void,
    required: boolean = false
  ) => ({
    className: "w-full px-4 py-2 border border-gray-300 rounded-lg shadow-sm focus:outline-none focus:ring-2 focus:ring-blue-500 bg-white",
    value: value,
    onChange: onChange,
    disabled: isLoading,
    required: required
  });

  return (
    <form onSubmit={handleSubmit} className="grid grid-cols-1 md:grid-cols-2 gap-x-6 gap-y-4">
      {/* Row 1 */}
      <div className="space-y-1">
        <label htmlFor="name" className="block text-sm font-medium text-gray-700">Customer Name *</label>
        <input id="name" {...getInputProps(name, (e) => setName(e.target.value), 'e.g., Mr. Amar', 'text', true)} />
      </div>
      <div className="space-y-1">
        <label htmlFor="callingNumber" className="block text-sm font-medium text-gray-700">Your Calling Number *</label>
        <input id="callingNumber" {...getInputProps(callingNumber, (e) => setCallingNumber(e.target.value), 'e.g., +15550001111', 'tel', true)} />
      </div>

      {/* Row 2 */}
      <div className="space-y-1">
        <label htmlFor="phoneNumber" className="block text-sm font-medium text-gray-700">Customer Phone Number *</label>
        <input id="phoneNumber" {...getInputProps(phoneNumber, handlePhoneNumberChange, 'e.g., 966501234567', 'tel', true)} />
      </div>
      <div className="space-y-1">
        <label htmlFor="licensePlate" className="block text-sm font-medium text-gray-700">License Plate *</label>
        <input id="licensePlate" {...getInputProps(licensePlate, handleLicensePlateChange, 'e.g., 7184LBB', 'text', true)} />
      </div>

      {/* Row 3 */}
      <div className="space-y-1">
        <label htmlFor="vehicleName" className="block text-sm font-medium text-gray-700">Vehicle Name *</label>
        <select id="vehicleName" {...getSelectProps(vehicleName, handleVehicleNameChange, true)}>
          <option value="" disabled>Select Vehicle Name...</option>
          {vehicleNamesList.map((vehicle) => (
            <option key={vehicle} value={vehicle}>{vehicle}</option>
          ))}
        </select>
      </div>
      <div className="space-y-1">
        <label htmlFor="language" className="block text-sm font-medium text-gray-700">Language *</label>
        <select id="language" {...getSelectProps(language, handleLanguageChange, true)}>
          <option value="" disabled>Select Language...</option>
          {languages.map((lang) => (
            <option key={lang} value={lang}>{lang}</option>
          ))}
        </select>
      </div>

      {/* Row 4 */}
      <div className="space-y-1">
        <label htmlFor="botName" className="block text-sm font-medium text-gray-700">Bot Name *</label>
        <input id="botName" {...getInputProps(botName, (e) => setBotName(e.target.value), 'e.g., Salma', 'text', true)} />
      </div>
      <div className="space-y-1">
        <label htmlFor="botIntro" className="block text-sm font-medium text-gray-700">Greetings *</label>
        <input id="botIntro" {...getInputProps(botIntro, (e) => setBotIntro(e.target.value), 'e.g., We are glad to call you...', 'text', true)} />
      </div>
      
      {/* Notification Preferences */}
      <div className="space-y-1 md:col-span-2 mt-2">
        <label className="block text-sm font-medium text-gray-700 mb-2">Notification Preferences:</label>
        <div className="flex items-center space-x-6">
          <div className="flex items-center">
            <input
              id="sendSms"
              type="checkbox"
              checked={sendSms}
              onChange={(e) => setSendSms(e.target.checked)}
              className="h-4 w-4 text-blue-600 border-gray-300 rounded focus:ring-blue-500"
              disabled={isLoading}
            />
            <label htmlFor="sendSms" className="ml-2 block text-sm text-gray-900">Send SMS</label>
          </div>
          <div className="flex items-center">
            <input
              id="sendWhatsapp"
              type="checkbox"
              checked={sendWhatsapp}
              onChange={(e) => setSendWhatsapp(e.target.checked)}
              className="h-4 w-4 text-blue-600 border-gray-300 rounded focus:ring-blue-500"
              disabled={isLoading}
            />
            <label htmlFor="sendWhatsapp" className="ml-2 block text-sm text-gray-900">Send WhatsApp</label>
          </div>
        </div>
      </div>

      {/* Toggle for Dynamic Labels Section */}
      <div className="md:col-span-2 mt-4">
        <div className="flex items-center justify-between p-3 bg-gray-100 rounded-lg border border-gray-200 cursor-pointer hover:bg-gray-200"
            onClick={handleToggleCustomFields}>
          <span className="font-medium text-gray-700">
            {showCustomFields ? 'Hide Custom Fields' : 'Show Custom Fields (Optional)'}
          </span>
          <svg
            className={`w-5 h-5 text-gray-500 transform transition-transform duration-200 ${showCustomFields ? 'rotate-180' : ''}`}
            fill="none"
            stroke="currentColor"
            viewBox="0 0 24 24"
            xmlns="http://www.w3.org/2000/svg"
          >
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M19 9l-7 7-7-7"></path>
          </svg>
        </div>
      </div>

      {/* Dynamic Labels Section - Conditionally rendered */}
      {showCustomFields && (
        <div className="md:col-span-2 space-y-3 p-4 border border-gray-200 rounded-lg bg-gray-50 transition-all duration-300 ease-in-out">
          <label className="block text-lg font-medium text-gray-700 mb-3">Add Custom Label/Value Pairs</label>
          {customLabels.length === 0 && (
            <p className="text-sm text-gray-600 italic">No custom fields added yet. Click 'Add label' to start.</p>
          )}
          {customLabels.map((item, index) => (
            <div key={index} className="flex items-center gap-3">
              <div className="flex-1">
                <label htmlFor={`custom-label-${index}`} className="sr-only">Label</label>
                <input
                  id={`custom-label-${index}`}
                  type="text"
                  placeholder="Label"
                  className="w-full px-4 py-2 border border-gray-300 rounded-lg shadow-sm focus:outline-none focus:ring-2 focus:ring-blue-500"
                  value={item.label}
                  onChange={(e) => handleCustomLabelChange(index, 'label', e)}
                  disabled={isLoading}
                />
              </div>
              <div className="flex-1">
                <label htmlFor={`custom-value-${index}`} className="sr-only">Value</label>
                {/* For 'Language' label, use a select dropdown; otherwise, use text input */}
                {item.label.toLowerCase() === 'language' ? (
                  <select
                    id={`custom-value-${index}`}
                    className="w-full px-4 py-2 border border-gray-300 rounded-lg shadow-sm focus:outline-none focus:ring-2 focus:ring-blue-500 bg-white"
                    value={item.value}
                    onChange={(e) => handleCustomLabelChange(index, 'value', e)}
                    disabled={isLoading}
                  >
                    <option value="" disabled>Select Value...</option>
                    {languages.map((lang) => (
                      <option key={lang} value={lang}>{lang}</option>
                    ))}
                  </select>
                ) : (
                  <input
                    id={`custom-value-${index}`}
                    type="text"
                    placeholder="Value"
                    className="w-full px-4 py-2 border border-gray-300 rounded-lg shadow-sm focus:outline-none focus:ring-2 focus:ring-blue-500"
                    value={item.value}
                    onChange={(e) => handleCustomLabelChange(index, 'value', e)}
                    disabled={isLoading}
                  />
                )}
              </div>
              <button
                type="button"
                onClick={() => handleRemoveLabel(index)}
                className="p-2 text-red-600 hover:text-red-800 rounded-full focus:outline-none focus:ring-2 focus:ring-red-500"
                disabled={isLoading}
              >
                <svg xmlns="http://www.w3.org/2000/svg" className="h-5 w-5" viewBox="0 0 20 20" fill="currentColor">
                  <path fillRule="evenodd" d="M9 2a1 1 0 00-.894.553L7.382 4H4a1 1 0 000 2v10a2 2 0 002 2h8a2 2 0 002-2V6a1 1 0 100-2h-3.382l-.724-1.447A1 1 0 0011 2H9zM7 8a1 1 0 012 0v6a1 1 0 11-2 0V8zm6 0a1 1 0 11-2 0v6a1 1 0 112 0V8z" clipRule="evenodd" />
                </svg>
              </button>
            </div>
          ))}
          <button
            type="button"
            onClick={handleAddLabel}
            className="mt-3 flex items-center justify-center px-4 py-2 border border-transparent text-sm font-medium rounded-md text-blue-700 bg-blue-100 hover:bg-blue-200 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500"
            disabled={isLoading}
          >
            <svg xmlns="http://www.w3.org/2000/svg" className="h-5 w-5 mr-2" viewBox="0 0 20 20" fill="currentColor">
              <path fillRule="evenodd" d="M10 5a1 1 0 011 1v3h3a1 1 0 110 2h-3v3a1 1 0 11-2 0v-3H6a1 1 0 110-2h3V6a1 1 0 011-1z" clipRule="evenodd" />
            </svg>
            Add label
          </button>
        </div>
      )}

      {/* Error Message Display - Spanning across columns */}
      {error && <p className="md:col-span-2 mt-1 text-sm text-red-600 text-center">{error}</p>}

      {/* Submit Button - Spanning across columns */}
      <div className="md:col-span-2 mt-4">
        <button
          type="submit"
          className="w-full py-2.5 px-4 bg-blue-600 text-white font-semibold rounded-lg hover:bg-blue-700 transition-all duration-300 disabled:opacity-50"
          disabled={isLoading}
        >
          {isLoading ? '🔄 Connecting...' : 'Call Now'}
        </button>
      </div>
    </form>
  );
};

// --- AlMajdouieCallApp Component (Now directly manages the 2/3 - 1/3 layout) ---
const AlMajdouieCallApp: React.FC = () => {
  const [isLoading, setIsLoading] = useState(false);
  const [callStatus, setCallStatus] = useState<null | 'success' | 'error'>(null);
  const [callSid, setCallSid] = useState('');
  const [lastError, setLastError] = useState<string | null>(null);

  const makeCall = async (payload: CallPayload) => {
    setIsLoading(true);
    setCallStatus(null);
    setLastError(null);
    console.log("Sending payload:", payload);

    try {
      const response = await fetch('https://pipecat-voice-bot-dev.ispgnet.com/make-AlMajdouie-call', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify(payload),
      });

      let data;
      try {
        data = await response.json();
      } catch (jsonError) {
        const textError = await response.text();
        console.error('Non-JSON response:', textError);
        data = { message: textError || 'Failed to parse response' };
      }

      if (data.success === true) {
        setCallStatus('success');
        setCallSid(data.call_sid || 'N/A');
        console.log("Call initiated successfully", data);
      } else {
        console.error('API Error:', response.status, data);
        setCallStatus('error');
        setLastError(data?.message || data?.detail || `API returned status ${response.status}`);
      }
    } catch (error) {
      console.error('Fetch Error making call:', error);
      setCallStatus('error');
      setLastError(error instanceof Error ? error.message : 'An unknown network error occurred.');
    } finally {
      setIsLoading(false);
    }
  };

  return (
    <>
      {/* 1. Header Section */}
      <div className="flex items-center gap-3 bg-white p-4 border-b border-gray-200 shadow-sm">
        <h1 className="text-xl font-bold">📲 Invisible Assistant</h1>
      </div>

      {/* 2. Main Content Area (2/3 + 1/3 split) */}
      <div className="min-h-[calc(100vh-70px)] flex flex-col items-center pt-10 pb-6 bg-gray-100 px-4">
        {/* This div acts as the container for the 2/3 and 1/3 sections */}
        <div className="max-w-7xl w-full mx-auto grid grid-cols-1 lg:grid-cols-3 gap-6">
          
          {/* Outbound Call Section (Left - 2/3rd space) */}
          <div className="lg:col-span-2 p-6 bg-white rounded-lg shadow-lg">
            <h2 className="text-2xl font-semibold text-gray-800 mb-6 text-center">Make an Outbound Call</h2>
            <CallForm onSubmit={makeCall} isLoading={isLoading} />
          </div>

          {/* Inbound Call Section (Right - 1/3rd space) */}
          <div className="lg:col-span-1"> {/* The InboundInfoCard already has styling, so minimal here */}
            <InboundInfoCard />
          </div>

        </div> {/* End of main content grid */}

        {/* 3. Status Messages Section (below the main content, spanning full width) */}
        {callStatus === 'success' && (
          <div className="mt-6 p-4 bg-green-100 text-green-800 rounded-lg shadow-md text-center w-full max-w-7xl">
            ✅ Call initiated successfully! <br />
            <span className="text-sm">Call SID: {callSid}</span>
          </div>
        )}
        {callStatus === 'error' && (
          <div className="mt-6 p-4 bg-red-100 text-red-800 rounded-lg shadow-md text-center w-full max-w-7xl">
            Unsuccessfull <br />
            {lastError && <span className="text-sm font-medium">Details: {lastError}</span>}
          </div>
        )}
      </div>
    </>
  );
};

export default AlMajdouieCallApp;